import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../../contexts/auth.context';
import {
  getAllShopHours,
  createShopHour,
  getPromos,
  deleteShopHour,
  updateShopHour,
} from '../../apis/atlantis.api';
import ShopHourList from './ShopHourList';
import CreateShopHourModal from './CreateShopHourModal';
import EditShopHourModal from './EditShopHourModal';

const ShopHours = () => {
  const [hours, setHours] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedShopHour, setSelectedShopHour] = useState(null);
  const [activePromo, setActivePromo] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const { auth } = useAuth();
  const authToken = useRef(auth?.idToken);

  const fetchHours = useCallback(async () => {
    if (!activePromo?.promoId) return;
    setIsLoading(true);
    try {
      const res = await getAllShopHours(activePromo.promoId, authToken.current);
      setHours(res || []);
    } catch (e) {
      console.error('Error fetching shop hours:', e);
    } finally {
      setIsLoading(false);
    }
  }, [activePromo?.promoId]);

  useEffect(() => {
    authToken.current = auth?.idToken;
    fetchActivePromo();
  }, [auth?.idToken]);

  useEffect(() => {
    if (activePromo) {
      fetchHours();
    }
  }, [activePromo, fetchHours]);

  const fetchActivePromo = async () => {
    try {
      const promos = await getPromos(authToken.current);
      const active = promos?.find(p => p.status === 'ACTIVE');
      setActivePromo(active);
    } catch (e) {
      console.error('Error fetching active promo:', e);
    }
  };

  const handleCreate = async shopHourData => {
    try {
      const res = await createShopHour(activePromo.promoId, shopHourData, authToken.current);
      console.log(res);
      await fetchHours();
    } catch (e) {
      console.error('Error creating shop hour:', e);
    }
  };

  const handleDelete = async (shopHourId, isRunning) => {
    const confirmMessage = isRunning
      ? 'Are you sure you want to delete? It will discontinue the shop'
      : 'Are you sure you want to delete this shop hour?';

    if (!window.confirm(confirmMessage)) {
      return;
    }

    setIsLoading(true);
    try {
      await deleteShopHour(activePromo.promoId, shopHourId, authToken.current);
      await fetchHours(); // Refresh the list after deletion
    } catch (e) {
      console.error('Error deleting shop hour:', e);
    } finally {
      setIsLoading(false);
    }
  };

  const handleEdit = shopHour => {
    setSelectedShopHour(shopHour);
    setShowEditModal(true);
  };

  const handleUpdate = async (shopHourId, shopHourData) => {
    try {
      await updateShopHour(activePromo.promoId, shopHourId, shopHourData, authToken.current);
      await fetchHours();
    } catch (e) {
      console.error('Error updating shop hour:', e);
      throw e;
    }
  };

  return (
    <div className="container pt-3" style={{ overflow: 'scroll' }}>
      <div className="d-flex justify-content-between flex-wrap flex-md-nowrap align-items-center mb-3 border-bottom">
        <h4 className="">Shop Hours</h4>
        <div className="btn-toolbar mb-2 mb-md-0">
          <div className="btn-group me-2">
            <button
              type="button"
              className="btn btn-sm btn-primary"
              onClick={() => setShowModal(true)}
            >
              Create Shop Hour
            </button>
          </div>
        </div>
      </div>
      <ShopHourList
        hours={hours}
        isLoading={isLoading}
        onDelete={handleDelete}
        onEdit={handleEdit}
      />
      <CreateShopHourModal
        show={showModal}
        onHide={() => setShowModal(false)}
        onCreate={handleCreate}
        token={authToken.current}
      />
      <EditShopHourModal
        show={showEditModal}
        onHide={() => {
          setShowEditModal(false);
          setSelectedShopHour(null);
        }}
        onUpdate={handleUpdate}
        shopHour={selectedShopHour}
        token={authToken.current}
      />
    </div>
  );
};

export default ShopHours;
